import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import Topbar from "@/components/Topbar";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle, XCircle, EnvelopeSimple } from "@phosphor-icons/react";

/**
 * v3.2.40 · Landing page for the emailed `/verify?token=...` link.
 * Runs once on mount, shows success or expired-link state.
 *
 * v3.2.58 · When verification succeeds we call the AuthContext
 * `refresh()` so the yellow "verify your email" banner rendered by
 * the app shell disappears in the same paint — otherwise it lingers
 * until the user manually reloads even though the success card is
 * on-screen (mirroring the exact bug reported in the screenshot).
 */
export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState("busy");   // busy | success | error
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  // v3.2.40 · Guard against React.StrictMode double-invoke that would
  // consume the same token twice and paint the (correctly-verified)
  // success state as a spurious "already used" error.
  const consumed = useRef(false);

  useEffect(() => {
    const token = params.get("token");
    if (!token) { setState("error"); setError("Missing verification token."); return; }
    if (consumed.current) return;
    consumed.current = true;
    (async () => {
      try {
        const { data } = await api.post("/auth/verify-email", { token });
        setEmail(data.email || "");
        setState("success");
        // Kick the AuthContext so the yellow verify-email banner in
        // the app shell drops the instant we paint "All set!".
        refresh();
      } catch (e) {
        setError(e.response?.data?.detail || "Verification failed.");
        setState("error");
      }
    })();
  }, [params, refresh]);

  return (
    <div className="industrial min-h-screen bg-[#0A0C10]">
      <Topbar />
      <div className="max-w-2xl mx-auto px-6 md:px-10 py-16">
        <div className="label-tag">// verify email</div>
        <h1 className="mt-2 font-display text-4xl md:text-5xl font-black tracking-tighter"
            data-testid="verify-email-heading">
          {state === "busy" ? "Verifying…"
            : state === "success" ? "Email verified"
            : "Couldn't verify"}
        </h1>

        <div className="mt-8 border border-white/10 p-6 md:p-8"
             data-testid="verify-email-card">
          <div className="flex items-start gap-4">
            {state === "success" && <CheckCircle size={40} weight="fill" className="text-emerald-400" />}
            {state === "error" && <XCircle size={40} weight="fill" className="text-[#FF3B30]" />}
            {state === "busy" && <EnvelopeSimple size={40} weight="duotone" className="text-[#00E5FF] animate-pulse" />}
            <div className="flex-1">
              {state === "success" && (
                <>
                  <div className="text-white font-display text-xl font-black" data-testid="verify-email-success">
                    All set!
                  </div>
                  <div className="mt-2 text-sm text-gray-300">
                    {email && <><span className="font-mono text-white">{email}</span> is verified. </>}
                    You can now post jobs, submit proposals, and make payments.
                  </div>
                </>
              )}
              {state === "error" && (
                <div className="text-[#FF3B30] text-sm" data-testid="verify-email-error">
                  {error}
                </div>
              )}
              {state === "busy" && (
                <div className="text-gray-400 text-sm">Confirming the link with our servers…</div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-8 flex items-center gap-3">
          {state === "success" && (
            <button onClick={() => nav("/")}
              className="btn-primary text-sm py-2"
              data-testid="verify-continue-btn">
              Continue →
            </button>
          )}
          {state === "error" && (
            <Link to="/settings" className="btn-primary text-sm py-2"
                  data-testid="verify-resend-link">
              Request a new link
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
